import React, { useState, useEffect, useCallback } from "react";
import { Section as SectionEntity, ListItem as ListItemEntity, List as ListEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, MoreVertical, Edit2, Trash2, ArrowUp, ArrowDown, CheckSquare, Square } from "lucide-react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { groupBy } from 'lodash';
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useData } from "../DataContext";
import type { Section, List, ListItem } from "@/types";

import ListItemComponent from "./ListItemComponent";

interface SectionComponentProps {
  section: Section;
  listId: string;
  onMove: (sectionId: string, direction: 'up' | 'down') => void;
  index: number;
  totalItems: number;
  allLists: List[];
  filterUnselected: boolean;
  isPinned?: boolean;
}

export default function SectionComponent({
  section,
  listId,
  onMove,
  index,
  totalItems,
  allLists,
  filterUnselected,
  isPinned = true,
}: SectionComponentProps) {
  const { folders, lists, sections, listItems, inheritedCompletions, runOptimisticUpdate, setOptimisticData } = useData();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(section.title || "");
  const [newItemContent, setNewItemContent] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const isNativeSection = section.list_id === listId;
  const isTemporarySection = section.id.toString().startsWith('temp-');

  // Get all unique section titles from the app for suggestions
  const existingSectionTitles = React.useMemo(() => {
    const titles = sections
      .filter(s => s.title && s.title.trim())
      .map(s => s.title!.trim())
      .filter((t, i, arr) => arr.indexOf(t) === i);
    return titles.sort();
  }, [sections]);

  useEffect(() => {
    setTitle(section.title || "");
    if (isNativeSection && isTemporarySection) {
      setIsEditingTitle(true);
    }
  }, [section, isNativeSection, isTemporarySection]);

  const isItemCompleted = useCallback((item: ListItem) => {
    if (item.isInherited) {
      const serverComp = inheritedCompletions?.find(c => c.list_id === listId && c.item_id === item.id);
      if (serverComp !== undefined) return serverComp.completed;
      const inheritanceKey = `${item.id}_${listId}`;
      try {
        const states = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
        return Boolean(states[inheritanceKey]);
      } catch {
        return false;
      }
    }
    return item.completed;
  }, [listId, inheritedCompletions]);
  
  const allItemsInSection = section.items || [];
  const unselectedItemsInSection = allItemsInSection.filter(item => !isItemCompleted(item));
  const itemsToRender = filterUnselected ? unselectedItemsInSection : allItemsInSection;
  
  const allNativeItemsInThisSection = allItemsInSection
    .filter(item => !item.isInherited)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  const displayedInheritedItems = itemsToRender.filter(item => item.isInherited);
  const inheritedItemsGroups = Object.values(groupBy(displayedInheritedItems, 'sourceListTitle'));

  const handleReorder = (newOrder: ListItem[]) => {
    const originalState = { listItems: [...listItems] };

    const task = async () => {
      await Promise.all(newOrder.map((item, idx) => ListItemEntity.update(item.id, { position: idx })));
    };

    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: [...sections],
      listItems: listItems.map(item => {
        const newItem = newOrder.find(ni => ni.id === item.id);
        return newItem ? { ...item, position: newOrder.indexOf(newItem) } : item;
      })
    };

    setOptimisticData(newState);
    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
  };

  const handleTitleSave = async () => {
    if (!isNativeSection || isTemporarySection || title === section.title) {
      setIsEditingTitle(false);
      setShowSuggestions(false);
      return;
    }
    
    const originalState = { sections: [...sections] };
    const task = async () => SectionEntity.update(section.id, { title: title.trim() || null });
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: sections.map(s => 
        s.id === section.id ? { ...s, title: title.trim() || null } : s
      ),
      listItems: [...listItems]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, ...originalState, listItems } });
    setIsEditingTitle(false);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setTitle(suggestion);
    setShowSuggestions(false);
    if (isNativeSection && !isTemporarySection) {
      const originalState = { sections: [...sections] };
      const task = async () => SectionEntity.update(section.id, { title: suggestion.trim() });
      const newState = {
        folders: [...folders],
        lists: [...lists],
        sections: sections.map(s => 
          s.id === section.id ? { ...s, title: suggestion.trim() } : s
        ),
        listItems: [...listItems]
      };
      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, ...originalState, listItems } });
    }
    setIsEditingTitle(false);
  };

  const handleAddItem = async (content: string, forceCreateSection = false) => {
    if (!content.trim()) return;

    if (!isNativeSection || forceCreateSection) {
      const maxPosition = sections.filter(s => s.list_id === listId).length;
      const newSectionId = `temp-section-${Date.now()}`;
      
      const sectionTask = async () => {
        const newSection = await SectionEntity.create({
          list_id: listId,
          title: section.title || '',
          position: maxPosition
        });
        
        const newItem = await ListItemEntity.create({
          section_id: newSection.id,
          content: content.trim(),
          position: 0
        });
        
        setOptimisticData(prevData => ({
          ...prevData,
          sections: prevData.sections.map(s => 
            s.id === newSectionId ? { ...s, ...newSection } : s
          ).concat(prevData.sections.find(s => s.id === newSectionId) ? [] : [newSection]),
          listItems: [...prevData.listItems, newItem]
        }));
      };

      const newState = {
        folders: [...folders],
        lists: [...lists],
        sections: [...sections, {
          id: newSectionId,
          list_id: listId,
          title: section.title || null,
          position: maxPosition,
          items: []
        } as Section],
        listItems: [...listItems]
      };

      runOptimisticUpdate(sectionTask, { update: newState, originalState: { folders, lists, sections, listItems } });
      setNewItemContent("");
      return;
    }

    if (isTemporarySection) return;

    const originalState = { listItems: [...listItems] };
    const newItemId = `temp-${Date.now()}`;
    const newItem: ListItem = {
      id: newItemId,
      section_id: section.id,
      content: content.trim(),
      completed: false,
      isInherited: false,
      position: allNativeItemsInThisSection.length
    };

    const task = async () => {
      const createdItem = await ListItemEntity.create({
        section_id: section.id,
        content: content.trim(),
        position: allNativeItemsInThisSection.length
      });
      setOptimisticData(prevData => ({
        ...prevData,
        listItems: prevData.listItems.map(item => 
          item.id === newItemId ? { ...item, ...createdItem } : item
        )
      }));
    };
    
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: [...sections],
      listItems: [...listItems, newItem]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
    setNewItemContent("");
  };

  const handleDeleteSection = () => {
    if (!isNativeSection || isTemporarySection) return;
    
    const originalState = { sections: [...sections], listItems: [...listItems] };
    const task = async () => {
      await Promise.all(allNativeItemsInThisSection.map(item => ListItemEntity.delete(item.id)));
      await SectionEntity.delete(section.id);
    };
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: sections.filter(s => s.id !== section.id),
      listItems: listItems.filter(i => i.section_id !== section.id)
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, ...originalState } });
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const pastedText = e.clipboardData.getData('text');
    const lines = pastedText.split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length > 1) {
      e.preventDefault();
      
      if (!isNativeSection) {
        await handleAddItem(lines[0], true); 
        for (let i = 1; i < lines.length; i++) {
          await ListItemEntity.create({
            section_id: section.id,
            content: lines[i],
            position: i,
          });
        }
      } else if (!isTemporarySection) {
        const startPosition = allNativeItemsInThisSection.length;
        for (let i = 0; i < lines.length; i++) {
          await ListItemEntity.create({
            section_id: section.id,
            content: lines[i],
            position: startPosition + i,
          });
        }
      }
      
      setNewItemContent("");
      window.location.reload();
    }
  };

  const filteredSuggestions = existingSectionTitles.filter(suggestionTitle =>
    suggestionTitle.toLowerCase().includes(title.toLowerCase()) && 
    suggestionTitle !== title
  );

  return (
    <Card className="bg-white border-0 shadow-lg group" data-section-id={section.id}>
      <CardContent className="p-3 sm:p-4 md:p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          {isEditingTitle && isNativeSection ? (
            <div className="flex-1 relative">
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setShowSuggestions(e.target.value.length > 0);
                }}
                placeholder="Section title (optional)"
                className="font-semibold text-base sm:text-lg border-slate-200 px-3 h-auto focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0 focus-visible:border-blue-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') {
                    setTitle(section.title || "");
                    setIsEditingTitle(false);
                    setShowSuggestions(false);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (showSuggestions) return;
                    handleTitleSave();
                  }, 150);
                }}
                autoFocus
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {filteredSuggestions.slice(0, 5).map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSuggestionClick(suggestion);
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <h3 
              className={`font-semibold text-base sm:text-lg text-slate-900 break-words pr-2 ${isNativeSection && !isTemporarySection ? 'cursor-pointer hover:text-slate-600' : ''} transition-colors`}
              onClick={() => isNativeSection && !isTemporarySection && setIsEditingTitle(true)}
            >
              {section.title || "Untitled Section"}
            </h3>
          )}

          {isNativeSection && !isTemporarySection && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-slate-100 flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsEditingTitle(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Title
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {unselectedItemsInSection.length > 0 && (
                  <DropdownMenuItem onClick={async () => {
                    const nativeItems = unselectedItemsInSection.filter(item => !item.isInherited);
                    const inheritedItems = unselectedItemsInSection.filter(item => item.isInherited);
                    
                    if (nativeItems.length > 0) {
                      const originalState = { listItems: [...listItems] };
                      const task = async () => {
                        await SectionEntity.setItemsCompleted(section.id, true);
                      };
                      const newState = {
                        folders: [...folders],
                        lists: [...lists],
                        sections: [...sections],
                        listItems: listItems.map(item => 
                          item.section_id === section.id ? { ...item, completed: true } : item
                        )
                      };
                      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
                    }
                    
                    if (inheritedItems.length > 0) {
                      try {
                        const currentState = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
                        inheritedItems.forEach(item => {
                          const inheritanceKey = `${item.id}_${listId}`;
                          currentState[inheritanceKey] = true;
                          ListEntity.setInheritedItemCompleted(listId, item.id, true).catch(() => {});
                        });
                        localStorage.setItem('inheritedItemStates', JSON.stringify(currentState));
                        setOptimisticData(prevData => ({ ...prevData }));
                      } catch (e) {}
                    }
                  }}>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Select All in Section ({unselectedItemsInSection.length})
                  </DropdownMenuItem>
                )}
                {allItemsInSection.filter(item => isItemCompleted(item)).length > 0 && (
                  <DropdownMenuItem onClick={async () => {
                    const completedItems = allItemsInSection.filter(item => isItemCompleted(item));
                    const nativeItems = completedItems.filter(item => !item.isInherited);
                    const inheritedItems = completedItems.filter(item => item.isInherited);
                    
                    if (nativeItems.length > 0) {
                      const originalState = { listItems: [...listItems] };
                      const task = async () => {
                        await SectionEntity.setItemsCompleted(section.id, false);
                      };
                      const newState = {
                        folders: [...folders],
                        lists: [...lists],
                        sections: [...sections],
                        listItems: listItems.map(item => 
                          item.section_id === section.id ? { ...item, completed: false } : item
                        )
                      };
                      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
                    }
                    
                    if (inheritedItems.length > 0) {
                      try {
                        const currentState = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
                        inheritedItems.forEach(item => {
                          const inheritanceKey = `${item.id}_${listId}`;
                          delete currentState[inheritanceKey];
                          ListEntity.setInheritedItemCompleted(listId, item.id, false).catch(() => {});
                        });
                        localStorage.setItem('inheritedItemStates', JSON.stringify(currentState));
                        setOptimisticData(prevData => ({ ...prevData }));
                      } catch (e) {}
                    }
                  }}>
                    <Square className="w-4 h-4 mr-2" />
                    Deselect All in Section ({allItemsInSection.filter(item => isItemCompleted(item)).length})
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onMove(section.id, 'up')} disabled={index === 0}>
                  <ArrowUp className="w-4 h-4 mr-2" />
                  Move Up
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMove(section.id, 'down')} disabled={index === totalItems - 1}>
                  <ArrowDown className="w-4 h-4 mr-2" />
                  Move Down
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleDeleteSection}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {filterUnselected && allItemsInSection.length > 0 && (
          <div className="text-sm text-slate-500 mb-4 italic px-2">
            Showing {unselectedItemsInSection.length} unselected items out of {allItemsInSection.length}.
          </div>
        )}

        {/* Inherited Items */}
        {inheritedItemsGroups.map((group, groupIndex) => {
          if (group.length === 0) return null;
          return (
            <div key={groupIndex} className="mb-4 p-2 sm:p-3 md:p-4 border-l-4 border-blue-300 bg-blue-50/50 rounded-r-lg">
              {group[0].sourceListId ? (
                <Link 
                  to={createPageUrl(`EditList?id=${group[0].sourceListId}`)} 
                  className="text-xs sm:text-sm font-semibold text-blue-800 mb-2 sm:mb-3 block cursor-pointer hover:underline break-words"
                >
                  Inherited from "{group[0].sourceListTitle}"
                </Link>
              ) : (
                <p className="text-xs sm:text-sm font-semibold text-slate-600 mb-2 sm:mb-3 block break-words">
                  Inherited from "{group[0].sourceListTitle}"
                </p>
              )}

              <div className="space-y-1 sm:space-y-2">
                {group.map(item => (
                  <ListItemComponent
                    key={item.id}
                    item={item}
                    inherited={true}
                    listId={listId}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Native Items */}
        {isNativeSection && allNativeItemsInThisSection.length > 0 ? (
          isPinned ? (
            <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
              <AnimatePresence>
                {allNativeItemsInThisSection.map((item) => (
                  (filterUnselected ? !isItemCompleted(item) : true) && (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ListItemComponent item={item} />
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <Reorder.Group axis="y" values={allNativeItemsInThisSection} onReorder={handleReorder}>
              <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                <AnimatePresence>
                  {allNativeItemsInThisSection.map((item) => (
                    (filterUnselected ? !isItemCompleted(item) : true) && (
                      <Reorder.Item 
                        key={item.id} 
                        value={item}
                        className="cursor-grab active:cursor-grabbing"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItemComponent item={item} />
                      </Reorder.Item>
                    )
                  ))}
                </AnimatePresence>
              </div>
            </Reorder.Group>
          )
        ) : (
          isNativeSection && itemsToRender.length === 0 && <div className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4 px-2">No items in this section yet.</div>
        )}

        {/* Add Item */}
        {!isTemporarySection && (
          <div className="flex gap-2">
            <Input
              value={newItemContent}
              onChange={(e) => setNewItemContent(e.target.value)}
              placeholder="Add new item..."
              className="flex-1 border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddItem(newItemContent);
                }
              }}
              onPaste={handlePaste}
            />
            <Button 
              onClick={() => handleAddItem(newItemContent)}
              disabled={!newItemContent.trim()}
              size="icon"
              className="bg-blue-600 hover:bg-blue-700 flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Show message for temporary sections */}
        {isTemporarySection && (
          <div className="text-xs sm:text-sm text-slate-500 italic text-center py-4">
            Section is being created... Please wait.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
